import { type Variants } from 'framer-motion';

// Reduced motion variants (instant transitions)
// NOTE: opacity starts at 1 so crawlers always see content in the DOM
export const reducedMotionVariants: Variants = {
  hidden: { opacity: 1 },
  visible: { opacity: 1, transition: { duration: 0.01 } }
};

// Standard fade up animation - using translateY instead of y to prevent layout shifts
// NOTE: opacity starts at 1 so crawlers always index the text content.
// Only the transform provides the visual entrance effect.
export const fadeInUp: Variants = {
  hidden: { opacity: 1, transform: 'translateY(20px)' },
  visible: { opacity: 1, transform: 'translateY(0px)', transition: { duration: 0.6 } }
};

// Simple fade — starts visible for SEO, only animates transform
export const fadeIn: Variants = {
  hidden: { opacity: 1 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
};

// Container with stagger effect — visible by default for SEO
export const staggerContainer: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

// Reduced motion stagger container (no stagger)
export const reducedStaggerContainer: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { duration: 0.01 }
  }
};

// Scale in effect - using transform to prevent layout shifts
export const scaleIn: Variants = {
  hidden: { opacity: 1, transform: 'scale(0.95)' },
  visible: { opacity: 1, transform: 'scale(1)', transition: { duration: 0.5 } }
};

// Slide in from left
export const slideInLeft: Variants = {
  hidden: { opacity: 1, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } }
};

// Slide in from right
export const slideInRight: Variants = {
  hidden: { opacity: 1, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } }
};

// Bounce in
export const bounceIn: Variants = {
  hidden: { opacity: 1, scale: 0.3 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { 
      type: "spring",
      stiffness: 300,
      damping: 20
    } 
  }
};

// Card hover animation properties
export const cardHover = {
  scale: 1.02,
  boxShadow: '0 10px 30px -10px hsl(var(--primary) / 0.25)',
  transition: { duration: 0.2 }
};

// Reduced motion card hover (no scale)
export const reducedCardHover = {
  boxShadow: '0 10px 30px -10px hsl(var(--primary) / 0.25)',
  transition: { duration: 0.01 }
};

// Button tap animation
export const buttonTap = {
  scale: 0.95
};

// Helper function to get appropriate variants based on reduced motion preference
export const getVariants = (standardVariants: Variants, prefersReducedMotion: boolean): Variants => {
  return prefersReducedMotion ? reducedMotionVariants : standardVariants;
};

export const getStaggerVariants = (prefersReducedMotion: boolean): Variants => {
  return prefersReducedMotion ? reducedStaggerContainer : staggerContainer;
};

export const getCardHover = (prefersReducedMotion: boolean) => {
  return prefersReducedMotion ? reducedCardHover : cardHover;
};
